import React, { createContext, forwardRef, PropsWithChildren, useContext, useEffect, useState } from "react";
import { AsyncContentState, classNames, useObservableProperty } from "zeta-dom-react";
import { GridContainer, GridItem, GridItemProps, Icon, Loading } from ".";

const Context = createContext<{ current?: {} | null } | null>(null);

export interface CardListProps extends React.ComponentPropsWithoutRef<'div'> {
    asyncState?: AsyncContentState<any>;
    emptyView?: JSX.Element;
}

export const CardList = forwardRef<HTMLDivElement, CardListProps>((props, ref) => {
    const { asyncState, emptyView, children, ..._props } = props;
    const [container] = useState({});
    return (
        <Context.Provider value={container}>
            <GridContainer ref={ref} {..._props} className={classNames('eac-card-item-container', props.className)}>
                {(asyncState?.loading &&
                    <GridItem span="full">
                        <Loading />
                    </GridItem>
                ) ||
                    (!React.Children.count(children) && emptyView &&
                        <GridItem span="full">
                            {emptyView}
                        </GridItem>
                    ) || (
                        children)}
            </GridContainer>
        </Context.Provider>
    )
});

export function Card(props: Omit<GridItemProps, 'ref' | 'span'> & { collapsible?: boolean }) {
    const { collapsible, ..._props } = props;
    const [item] = useState({});
    const container = useContext(Context);
    const current = useObservableProperty(container || {}, 'current');

    let children = props.children as any[];
    let header = children?.[0]?.type === CardHeader ? children[0] : undefined;
    if (header) {
        children = children.slice(1);
    }

    useEffect(() => {
        if (container && container.current === undefined) {
            container.current = item;
        }
    }, [container, item]);

    return (
        <GridItem {..._props} span="full" className={classNames('eac-card-item', props.className, { collapsible, expanded: current === item })}>
            <div className="eac-card-item-header">
                <div className="eac-card-item-header-content">
                    {header}
                </div>
                {collapsible &&
                    <Icon icon="arrowRight" onClick={onClickToggle} />}
            </div>
            {children}
        </GridItem>
    );

    function onClickToggle() {
        if (container) {
            container.current = container.current === item ? null : item;
        }
    }
}

export function CardHeader(props: PropsWithChildren<{}>) {
    return (<>{props.children}</>);
}
